/**
 * CaseFormPage：命例添加/修改表单页（#/case-form 新增；#/case-form?edit=<chartId> 修改）
 * 字段：名称、阳历/阴历、年/月/日/时/分、地点、性别、闰月（阴历）、分类、备注、隐藏生辰开关
 */
import { useState } from 'react';
import type { BirthInput } from '@shared/types/api';
import { DEFAULT_CATEGORY } from '@shared/engine/chart-store';
import { useChartStore } from '../state/store';

export default function CaseFormPage() {
  const editId = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('edit');
  const cases = useChartStore((s) => s.cases);
  const categories = useChartStore((s) => s.categories);
  const addCase = useChartStore((s) => s.addCase);
  const updateCase = useChartStore((s) => s.updateCase);
  const editing = cases.find((c) => c.chartId === editId) ?? null;

  const [name, setName] = useState(editing?.name ?? '');
  const [calendarType, setCalendarType] = useState<'solar' | 'lunar'>(editing?.input.calendarType ?? 'solar');
  const [year, setYear] = useState(editing?.input.year ?? 1986);
  const [month, setMonth] = useState(editing?.input.month ?? 5);
  const [day, setDay] = useState(editing?.input.day ?? 5);
  const [hour, setHour] = useState(editing?.input.hour ?? 8);
  const [minute, setMinute] = useState(editing?.input.minute ?? 30);
  const [location, setLocation] = useState(editing?.input.location ?? '合肥');
  const [gender, setGender] = useState<'male' | 'female'>(editing?.input.gender ?? 'male');
  const [isLeapMonth, setIsLeapMonth] = useState(editing?.input.isLeapMonth ?? false);
  const [category, setCategory] = useState(editing?.category ?? DEFAULT_CATEGORY);
  const [remark, setRemark] = useState(editing?.remark ?? '');
  /** 隐藏生辰（默认关）：开启后命盘中宫隐姓名为匿名、时间三行与四柱置空；仅本地存储 */
  const [hideBirth, setHideBirth] = useState(editing?.hideBirth ?? false);

  const inputCls = 'w-20 rounded border border-slate-300 px-2 py-1.5 text-sm';
  const labelCls = 'flex items-center gap-1 text-sm text-slate-600';

  async function onSave() {
    const input: BirthInput = { calendarType, year, month, day, hour, minute, location, gender, isLeapMonth };
    if (editing) {
      await updateCase(editing.chartId, { input, name, remark, category, hideBirth });
    } else {
      await addCase(input, { name, remark, category, hideBirth });
    }
    window.location.hash = '#/cases';
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-3">
      <div className="rounded-lg bg-white p-4 shadow">
        <h2 className="mb-3 text-base font-medium text-slate-800">{editing ? '修改命例' : '添加命例'}</h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className={labelCls}>
            名称
            <input className="w-32 rounded border border-slate-300 px-2 py-1.5 text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="如：张先生" />
          </label>

          <div className="flex overflow-hidden rounded-md ring-1 ring-slate-300">
            {(['solar', 'lunar'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setCalendarType(t)}
                className={`px-3 py-1.5 text-sm ${calendarType === t ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                {t === 'solar' ? '阳历' : '阴历'}
              </button>
            ))}
          </div>

          <label className={labelCls}>
            年
            <input type="number" min={1900} max={2100} className={inputCls} value={year} onChange={(e) => setYear(Number(e.target.value))} />
          </label>
          <label className={labelCls}>
            月
            <input type="number" min={1} max={12} className={inputCls} value={month} onChange={(e) => setMonth(Number(e.target.value))} />
          </label>
          <label className={labelCls}>
            日
            <input type="number" min={1} max={31} className={inputCls} value={day} onChange={(e) => setDay(Number(e.target.value))} />
          </label>
          <label className={labelCls}>
            时
            <input type="number" min={0} max={23} className={inputCls} value={hour} onChange={(e) => setHour(Number(e.target.value))} />
          </label>
          <label className={labelCls}>
            分
            <input type="number" min={0} max={59} className={inputCls} value={minute} onChange={(e) => setMinute(Number(e.target.value))} />
          </label>
          <label className={labelCls}>
            地点
            <input className="w-28 rounded border border-slate-300 px-2 py-1.5 text-sm" value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>

          <label className={labelCls}>
            性别
            <select className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm" value={gender} onChange={(e) => setGender(e.target.value as 'male' | 'female')}>
              <option value="male">男</option>
              <option value="female">女</option>
            </select>
          </label>

          {calendarType === 'lunar' && (
            <label className={labelCls}>
              <input type="checkbox" checked={isLeapMonth} onChange={(e) => setIsLeapMonth(e.target.checked)} />
              闰月
            </label>
          )}

          <label className={labelCls}>
            分类
            <select className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className={labelCls}>
            备注
            <input className="w-40 rounded border border-slate-300 px-2 py-1.5 text-sm" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="自定义备注" />
          </label>

          <label className={labelCls}>
            隐藏生辰
            <button
              type="button"
              role="switch"
              aria-checked={hideBirth}
              onClick={() => setHideBirth((v) => !v)}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${hideBirth ? 'bg-slate-700' : 'bg-slate-300'}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${hideBirth ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </label>
        </div>

        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onSave} className="rounded bg-slate-800 px-5 py-2 text-sm font-medium text-white hover:bg-slate-700">
            保存
          </button>
          <button type="button" onClick={() => (window.location.hash = '#/cases')} className="rounded border border-slate-300 bg-white px-5 py-2 text-sm text-slate-600 hover:bg-slate-50">
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
